import { UserClaims } from 'app/@types/dto/user-claims.model';
import { User } from 'app/auth/models';
import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AuthenticationService } from 'app/auth/service';
import { HttpClient, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { UrlService } from '../services/datasources/url.service';
import { LocalStorageKeys, StorageService } from '../services/storage/storage.service';
import { BehaviorSubject, Observable, Subject, catchError, first } from 'rxjs';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { UserHttpService } from '@services/datasources/user-http.service';
import { FormulaireCallCompletion } from 'app/@types/shared/formulaire-call-completion';
import { AppRoutingService } from '@services/app-routing/app-routing.service';

export enum UserStatus {
    NONE = 'none',
    TERMS = 'terms',
    ONBOARDED = 'onboarded',
}

export interface CurrentUserStateModel extends BaseStateInterface {
    currentUser: User | undefined;
    currentUserClaims: UserClaims | undefined;
    apiCallResult: HttpStatusCode | undefined;
    userStatus: UserStatus;
}

/**
 * Organization picked with "Control" in the dedicated-admin console (sudoMod), with the account
 * that picked it. localStorage outlives the sign-in session (an expired session never runs the
 * logout cleanup), so the owner keeps another account signing in on the same browser from
 * starting inside that organization.
 */
export interface StoredSudoOrganization {
    orgId: string;
    email: string;
}

// Version 1 stored the bare orgId with no owner: getGlobalItem discards it on version mismatch.
export const SUDO_ORGANIZATION_STORAGE_VERSION = 2;

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace UserAction {
    export class Activate {
        static readonly type = '[CurrentUser UI] Activate CurrentUser';
        constructor() {
            // Like this ESLint is happy
        }
    }

    export class Update {
        static readonly type = '[CurrentUser UI] Update CurrentUser';
        constructor(public orgId?: string) {}
    }

    export class UpdateWithValue {
        static readonly type = '[CurrentUser UI] UpdateWithValue CurrentUser';
        constructor(public claims: UserClaims) {}
    }

    export class Deactivate {
        static readonly type = '[CurrentUser UI] Deactivate CurrentUser';
        constructor() {
            // Like this ESLint is happy
        }
    }

    export class UpdateOrganization {
        static readonly type = '[CurrentUser UI] Update Organization';
        constructor(
            public org: OrganizationDto,
            public isOnboarding: boolean,
            public callResultSignal?: Subject<FormulaireCallCompletion>,
        ) {}
    }

    export class UpdateUserStatus {
        static readonly type = '[CurrentUser UI] Update CurrentUserStatus';
        constructor(public status: UserStatus) {}
    }
}

@State<CurrentUserStateModel>({
    name: 'user',
    defaults: {
        currentUser: undefined,
        currentUserClaims: undefined,
        isInit: false,
        isLoading: false,
        apiCallResult: undefined,
        userStatus: UserStatus.NONE,
        errors: [],
    },
})
@Injectable()
export class UserState {
    constructor(
        private authenticationService: AuthenticationService,
        private http: HttpClient,
        private urlService: UrlService,
        private _authenticationService: AuthenticationService,
        private localStorageService: StorageService,
        private userService: UserHttpService,
        private store: Store,
        private appRouting: AppRoutingService,
    ) {}

    //----- store action -----//
    @Action(UserAction.Activate)
    activate(ctx: StateContext<CurrentUserStateModel>, _action: UserAction.Activate) {
        ctx.patchState({
            isLoading: true,
        });

        if (ctx.getState().isInit) {
            ctx.patchState({
                isLoading: false,
            });
            return;
        }

        this.authenticationService.currentUser$.subscribe((user) => {
            if (user) {
                ctx.patchState({
                    currentUser: user,
                    userStatus:
                        ctx.getState().userStatus === UserStatus.NONE && user?.has_accepted_terms === '1'
                            ? UserStatus.TERMS
                            : ctx.getState().userStatus,
                });
            } else {
                const errors = ctx.getState().errors;
                errors.push('Could not get Session User');
                ctx.patchState({
                    currentUser: undefined,
                    errors: errors,
                });
                return;
            }
        });

        const orgId = this.readSudoOrganization(ctx.getState().currentUser?.email);

        this.fetchUserClaim(ctx, orgId);

        this._authenticationService.currentUser$.subscribe((x) => {
            if (x === null) {
                this.disableUserClaim(ctx, []);
            }
        });
    }

    @Action(UserAction.UpdateOrganization)
    updateOrganizationClaims(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateOrganization) {
        this.userService.updateOrganization(action.org.uid ?? '', action.org).subscribe({
            next: (org) => {
                if (org) {
                    const currentClaims = ctx.getState().currentUserClaims;
                    if (!currentClaims) {
                        return;
                    }
                    const claims = { ...currentClaims };
                    claims.organization = org;
                    this.populateUserClaims(claims, 200, ctx);
                    const onboardingAwaiter = new BehaviorSubject<boolean | undefined>(undefined);
                    if (action.isOnboarding) {
                        onboardingAwaiter.subscribe({
                            next: (success) => {
                                if (success === undefined) {
                                    return;
                                }
                                if (success) {
                                    if (action.callResultSignal) {
                                        action.callResultSignal.next('orgSuccess');
                                        action.callResultSignal.complete();
                                    }
                                } else {
                                    if (action.callResultSignal) {
                                        action.callResultSignal.next('orgFail');
                                        action.callResultSignal.complete();
                                    }
                                }
                                onboardingAwaiter.complete();
                            },
                        });
                    } else {
                        if (action.callResultSignal) {
                            action.callResultSignal.next('orgSuccess');
                            action.callResultSignal.complete();
                        }
                    }
                }
            },
            error: (_e) => {
                if (action.callResultSignal) {
                    action.callResultSignal.next('orgFail');
                    action.callResultSignal.complete();
                }
                //todo handle errors if not in the form component
            },
        });
    }

    @Action(UserAction.Update)
    updateUserClaim(ctx: StateContext<CurrentUserStateModel>, action: UserAction.Update) {
        if (action.orgId) {
            this.localStorageService.setGlobalItem<StoredSudoOrganization>(
                LocalStorageKeys.sudoMod,
                { orgId: action.orgId, email: ctx.getState().currentUser?.email ?? '' },
                SUDO_ORGANIZATION_STORAGE_VERSION,
            );
        } else {
            this.fetchUserClaim(ctx, action.orgId);
        }
    }

    @Action(UserAction.Deactivate)
    deactivateUserClaim(ctx: StateContext<CurrentUserStateModel>, _action: UserAction.Deactivate) {
        this.disableUserClaim(ctx, []);
    }

    @Action(UserAction.UpdateWithValue)
    updateUserClaimWithValue(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateWithValue) {
        this.populateUserClaims(action.claims, 200, ctx);
    }

    @Action(UserAction.UpdateUserStatus)
    updateUserStatus(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateUserStatus) {
        ctx.patchState({
            userStatus: action.status,
        });
    }

    //-------------- internal functions ----------------//

    private populateUserClaims(
        claims: UserClaims,
        requestStatus: HttpStatusCode,
        ctx: StateContext<CurrentUserStateModel>,
    ) {
        if (!claims) {
            this.appRouting.navigateToLoginPage();
        }

        ctx.patchState({
            isInit: true,
            isLoading: false,
            errors: [],
            currentUserClaims: claims,
            userStatus:
                ctx.getState().userStatus === UserStatus.TERMS && claims?.organization?.isOnboarded
                    ? UserStatus.ONBOARDED
                    : ctx.getState().userStatus,
            apiCallResult: requestStatus,
        });
    }

    private disableUserClaim(ctx: StateContext<CurrentUserStateModel>, errors: string[]) {
        // Session-scoped routing context must not leak into the next sign-in
        // (silent logouts skip the full page reload that would otherwise reset it).
        this.appRouting.wantedRoute = undefined;
        this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
        ctx.patchState({
            isInit: false,
            isLoading: false,
            errors: errors,
            currentUserClaims: undefined,
        });
    }

    /**
     * The stored sudo organization, only for the account that picked it; a value picked by
     * another account (or with no owner) is dropped instead of being replayed.
     */
    private readSudoOrganization(email: string | undefined): string | undefined {
        const stored = this.localStorageService.getGlobalItem<StoredSudoOrganization>(
            LocalStorageKeys.sudoMod,
            SUDO_ORGANIZATION_STORAGE_VERSION,
        );
        if (!stored) {
            return undefined;
        }
        if (!email || stored.email.toLowerCase() !== email.toLowerCase()) {
            this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
            return undefined;
        }
        return stored.orgId;
    }

    public fetchUserClaim(ctx: StateContext<CurrentUserStateModel>, organization?: string): void {
        this.get(organization).subscribe({
            next: (response) => {
                const claims = response.body as UserClaims;
                if (organization && claims?.organization?.uid !== organization) {
                    // The stored sudo organization was not honoured by the backend (stale value
                    // from a previous session / another account): drop it so the next session
                    // activation does not replay it.
                    this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
                }
                this.populateUserClaims(claims, response.status, ctx);
            },
            error: (_err) => {
                this.authenticationService.logout(false).pipe(first()).subscribe();
                this.disableUserClaim(ctx, ['Could not get the userClaim']);
            },
        });
    }

    private get(organization?: string): Observable<HttpResponse<UserClaims>> {
        return this.http.get<UserClaims>(this.urlService.getUserClaimsUrl(organization), { observe: 'response' }).pipe(
            catchError((_err) => {
                throw new Error('GetUserClaimsFailed');
            }),
        );
    }

    //-------------- selectors ----------------//
    @Selector([UserState])
    static getCurrentUser(state: CurrentUserStateModel) {
        return state.currentUser;
    }

    @Selector([UserState])
    static getCurrentUserClaims(state: CurrentUserStateModel) {
        return state.currentUserClaims;
    }

    @Selector([UserState])
    static getResponseStatus(state: CurrentUserStateModel) {
        return state.apiCallResult;
    }

    @Selector([UserState])
    static getUserStatus(state: CurrentUserStateModel) {
        return state.userStatus;
    }
}
